import { useCallback, useEffect, useRef } from 'react';
import {
  applyDopplerLayerVisualState,
  getDopplerLayerVisualState,
} from '../utils/dopplerLayer.utils.js';

const isFrameIndexMissing = (frameIndex) => frameIndex === null || frameIndex === undefined;

export default function useDopplerFrameTransition({
  displayedFrameIndex,
  layersRef,
  onDisplayedFrameChange,
  onTransitionStateChange,
  syncVisibleLayerLoadingState: syncLayerLoadingState,
}) {
  const transitionRef = useRef(null);
  const currentFrameRef = useRef(displayedFrameIndex);
  const trailFrameRef = useRef(null);
  // Last opacity/zIndex written to each layer. Leaflet's setOpacity rewrites every tile of the layer
  // and prunes, so a frame step touches only the two or three layers whose state changes.
  const appliedVisualStateRef = useRef(new WeakMap());

  const stopTransition = useCallback(() => {
    const animationFrameId = transitionRef.current?.animationFrameId;
    if (animationFrameId !== null && animationFrameId !== undefined) {
      window.cancelAnimationFrame(animationFrameId);
    }

    transitionRef.current = null;
  }, []);

  const syncVisibleLayerLoadingState = useCallback(() => {
    syncLayerLoadingState(currentFrameRef.current, trailFrameRef.current);
  }, [syncLayerLoadingState]);

  const applySettledFrameState = useCallback(
    (currentFrame = currentFrameRef.current, trailFrame = trailFrameRef.current) => {
      layersRef.current.forEach(({ frameIndex, layer }) => {
        const { opacity, zIndex } = getDopplerLayerVisualState(
          frameIndex,
          currentFrame,
          trailFrame,
        );
        const appliedState = appliedVisualStateRef.current.get(layer);
        if (appliedState?.opacity === opacity && appliedState?.zIndex === zIndex) return;

        applyDopplerLayerVisualState(layer, opacity, zIndex);
        if (layer?.getContainer?.()) {
          appliedVisualStateRef.current.set(layer, { opacity, zIndex });
        }
      });
    },
    [layersRef],
  );

  const prepareFrameCollection = useCallback(
    (initialFrameIndex) => {
      stopTransition();
      currentFrameRef.current = initialFrameIndex;
      trailFrameRef.current = null;
      onTransitionStateChange?.(false);
    },
    [onTransitionStateChange, stopTransition],
  );

  const startTransition = useCallback(
    (fromFrame, toFrame) => {
      if (isFrameIndexMissing(fromFrame) || isFrameIndexMissing(toFrame) || fromFrame === toFrame) {
        return;
      }

      stopTransition();
      onTransitionStateChange?.(true);

      const animationFrameId = window.requestAnimationFrame(() => {
        transitionRef.current = null;
        trailFrameRef.current = fromFrame;
        currentFrameRef.current = toFrame;
        applySettledFrameState(toFrame, fromFrame);
        syncVisibleLayerLoadingState();
        onDisplayedFrameChange?.(toFrame);
        onTransitionStateChange?.(false);
      });

      transitionRef.current = { fromFrame, toFrame, animationFrameId };
    },
    [
      applySettledFrameState,
      onDisplayedFrameChange,
      onTransitionStateChange,
      stopTransition,
      syncVisibleLayerLoadingState,
    ],
  );

  const settleCurrentFrame = useCallback(() => {
    stopTransition();
    applySettledFrameState();
    syncVisibleLayerLoadingState();
    onTransitionStateChange?.(false);
  }, [
    applySettledFrameState,
    onTransitionStateChange,
    stopTransition,
    syncVisibleLayerLoadingState,
  ]);

  const requestFrameTransition = useCallback(
    (requestedFrameIndex) => {
      if (!layersRef.current.length || transitionRef.current) return;

      startTransition(currentFrameRef.current, requestedFrameIndex);
    },
    [layersRef, startTransition],
  );

  useEffect(() => {
    if (!layersRef.current.length || transitionRef.current) return;

    currentFrameRef.current = displayedFrameIndex;
    if (trailFrameRef.current === displayedFrameIndex) {
      trailFrameRef.current = null;
    }

    applySettledFrameState();
    syncVisibleLayerLoadingState();
  }, [applySettledFrameState, displayedFrameIndex, layersRef, syncVisibleLayerLoadingState]);

  useEffect(() => {
    return stopTransition;
  }, [stopTransition]);

  return {
    applySettledFrameState,
    prepareFrameCollection,
    requestFrameTransition,
    settleCurrentFrame,
    stopTransition,
    syncVisibleLayerLoadingState,
  };
}
